import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import { RequestService } from '../../services/RequestService'
import PVTFinished from '../PVT/PVTFinished'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #000;
`

const TimeReflexContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid
    ${({ roundFinished }) => (roundFinished ? '#EEBC3D' : '#D63056')};
  border-radius: 8px;
  height: 100px;
  width: 300px;

  span {
    font-size: 40px;
    color: #fff;
  }
`

const IconContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  z-index: 2;
`

const STATUS = {
  COUNTDOWN: 'countdown',
  SETTING: 'setting',
  GO: 'go',
  FINISHED_ROUND: 'finishedRound',
  END: 'end',
  SENT: 'sent',
}

const initialCountdown = 3
const intervalBetweenRounds = 1500
const maxReflexTime = 2000

const PVTWorkout = ({ onClose, pvtId, testTime }) => {
  const [status, setStatus] = useState(STATUS.COUNTDOWN)
  const [countdown, setCountdown] = useState(initialCountdown)
  const [initialTestTime, setInitialTestTime] = useState(null)
  const [initialTime, setInitialTime] = useState(null)
  const [timeNow, setTimeNow] = useState(null)
  const [randomInterval, setRandomInterval] = useState(null)
  const [reactions, setReactions] = useState([])
  const [pvtData, setPvtData] = useState(null)
  const [isClickAllowed, setIsClickAllowed] = useState(false)
  const [isFalseStart, setIsFalseStart] = useState(false)
  const [falseStartInProgress, setFalseStartInProgress] = useState(false)
  const timeoutRef = useRef(null)
  const datenowRef = useRef(null)
  const roundEndTimeoutRef = useRef(null)
  const { t } = useTranslation('common')

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }

    setInitialTestTime(Date.now())
    runTest()
  }, [countdown])

  useEffect(() => {
    if (status === STATUS.END) {
      const sendData = async () =>
        await RequestService.createPVT({ pvtId, reactions })

      const getPVTData = async () => {
        const { data } = await RequestService.getPvtUserInfo()
        setPvtData({ data })
      }

      sendData()
      getPVTData()
      setStatus(STATUS.SENT)
      return
    }
  }, [status])

  useEffect(() => {
    if (status === STATUS.FINISHED_ROUND) {
      let interval

      interval = setInterval(() => {
        runTest()
      }, intervalBetweenRounds)

      return () => clearInterval(interval)
    }
  }, [status])

  useEffect(() => {
    let animationFrame

    if (status === STATUS.GO && initialTime) {
      const update = () => {
        const dateNow = Date.now()
        datenowRef.current = dateNow
        setTimeNow(dateNow - initialTime)
        animationFrame = requestAnimationFrame(update)
      }

      update()
    } else {
      cancelAnimationFrame(animationFrame)
    }

    return () => cancelAnimationFrame(animationFrame)
  }, [status, initialTime])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (roundEndTimeoutRef.current) clearTimeout(roundEndTimeoutRef.current)
    }
  }, [])

  const handleClick = () => {
    if (status === STATUS.END) return

    if (!isClickAllowed && status !== STATUS.SETTING) return

    if (status === STATUS.GO) {
      if (roundEndTimeoutRef.current) {
        clearTimeout(roundEndTimeoutRef.current)
        roundEndTimeoutRef.current = null
      }

      setReactions(prev => [
        ...prev,
        {
          initialRoundTime: initialTime,
          interval: randomInterval,
          clickTime: datenowRef.current,
          result: timeNow,
        },
      ])

      setIsClickAllowed(false)
      setStatus(STATUS.FINISHED_ROUND)
      return
    }

    if (status === STATUS.SETTING) {
      if (falseStartInProgress) return
      setFalseStartInProgress(true)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      setReactions(prev => [
        ...prev,
        {
          initialRoundTime: initialTime,
          interval: randomInterval,
          clickTime: null,
          result: null,
        },
      ])

      setIsFalseStart(true)
      setTimeout(() => {
        setIsFalseStart(false)
        setFalseStartInProgress(false)
        runTest()
      }, 2000)
      return
    }

    runTest()
  }

  useEffect(() => {
    const handleKeyPress = event => {
      if (event.code === 'Space') {
        event.preventDefault()
        handleClick()
      }
    }

    window.addEventListener('keydown', handleKeyPress)

    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [handleClick])

  const runTest = useCallback(() => {
    setStatus(STATUS.SETTING)
    setIsClickAllowed(false)
    setIsFalseStart(false)
    setTimeNow(null)

    if (initialTestTime && Date.now() > initialTestTime + testTime) {
      setStatus(STATUS.END)
      return
    }

    const interval = Math.floor(Math.random() * 4001) + 1000;
    setRandomInterval(interval)

    timeoutRef.current = setTimeout(() => {
      setInitialTime(Date.now())
      setStatus(STATUS.GO)
      setIsClickAllowed(true)

      roundEndTimeoutRef.current = setTimeout(() => {
        setIsClickAllowed(false)
        setStatus(STATUS.FINISHED_ROUND)
      }, maxReflexTime)
    }, interval)
  }, [initialTestTime])

  if (status === STATUS.SENT) {
    return (
      <PVTFinished
        done={pvtData?.data.done}
        pending={pvtData?.data.pending}
      />
    )
  }

  return (
    <Container onClick={handleClick}>
      <IconContainer onClick={onClose}>
        <CloseIcon sx={{ color: '#FFF' }} />
      </IconContainer>
      <>
        {status === STATUS.COUNTDOWN && (
          <span style={{ fontSize: 80, color: '#FFF' }}>{countdown}</span>
        )}

        {status === STATUS.END ? (
          <span
            style={{
              fontSize: 24,
              color: '#FFF',
            }}
          >
            {t('pvt.sending')}
          </span>
        ) : (
          <>
            {countdown <= 0 && (
              <TimeReflexContainer
                roundFinished={status === STATUS.FINISHED_ROUND}
              >
                {isFalseStart ? (
                  <span style={{ color: '#D63056', fontSize: 30 }}>
                    {t('pvt.false_start')}
                  </span>
                ) : (
                  timeNow > 0 && <span>{timeNow}</span>
                )}
              </TimeReflexContainer>
            )}
          </>
        )}
      </>
    </Container>
  )
}

export default PVTWorkout
